import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null); // null = checking, false = no, object = yes
  const [token, setToken] = useState(localStorage.getItem("hl_token"));

  useEffect(() => {
    if (!token) {
      setAdmin(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        setAdmin(res.data);
        try { localStorage.setItem("hl_admin", JSON.stringify(res.data)); } catch { /* noop */ }
      })
      .catch((err) => {
        // Offline / server unreachable: stay signed in with the last known admin profile so
        // in-progress work (e.g. the offline Score Keeper) survives a refresh. Only a real
        // auth rejection (has an HTTP response) should log the admin out.
        const offline = !navigator.onLine || err?.message === "Network Error" || !err?.response;
        if (offline) {
          let cached = null;
          try { cached = JSON.parse(localStorage.getItem("hl_admin") || "null"); } catch { cached = null; }
          setAdmin(cached || { offline: true });
          return;
        }
        localStorage.removeItem("hl_token");
        localStorage.removeItem("hl_admin");
        setToken(null);
        setAdmin(false);
      });
  }, [token]);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("hl_token", res.data.token);
    try { localStorage.setItem("hl_admin", JSON.stringify(res.data.user)); } catch { /* noop */ }
    setToken(res.data.token);
    setAdmin(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("hl_token");
    localStorage.removeItem("hl_admin");
    setToken(null);
    setAdmin(false);
  }, []);

  const applyToken = useCallback((newToken, user) => {
    localStorage.setItem("hl_token", newToken);
    setToken(newToken);
    if (user) setAdmin(user);
  }, []);

  const value = useMemo(() => ({ admin, login, logout, applyToken }), [admin, login, logout, applyToken]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
